// src/components/CompanyCreationTemplate.tsx
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  GlobalStyles,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { TypographyProps } from "@mui/material/Typography";
import { createData, fetchData } from "api/apiHelpers";
import { useSnackbar } from "notistack";
import React, { ChangeEvent, useEffect, useState } from "react";
import { useTranslation } from "../../i18n";
import { useNavigate } from "react-router-dom";
import theme from "theme";
import { Environment, PlatformUser, Sector } from "types";
import logger from "utils/logger";

// ---------- Global Fade-In Styles ----------
const FadeInStyles = (
  <GlobalStyles
    styles={`
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
    `}
  />
);

// ---------- TypewriterTitle Component ----------
// Reveals the provided text character by character (using a single base delay for all characters)
interface TypewriterTitleProps {
  text: string;
  variant?: TypographyProps["variant"];
  baseDelay?: number;
}
export const TypewriterTitle: React.FC<TypewriterTitleProps> = ({
  text,
  variant = "h4",
  baseDelay = 50,
}) => {
  const letters = text.split("");
  const [letterCount, setLetterCount] = useState(0);

  useEffect(() => {
    if (letterCount < letters.length) {
      const timer = setTimeout(() => {
        setLetterCount(letterCount + 1);
      }, baseDelay);
      return () => clearTimeout(timer);
    }
  }, [letterCount, letters, baseDelay]);

  const displayedText = letters.slice(0, letterCount).join("");
  return (
    <ReflectionText
      text={displayedText}
      variant={variant}
      // sx={{
      //   position: "relative",
      //   display: "inline-block",
      //   overflow: "hidden",
      //   whiteSpace: "pre-wrap",
      //   lineHeight: 1.5,

      //   color: theme.palette.custom.textPrimary,
      // }}
    ></ReflectionText>
  );
};

// ---------- ReflectionText Component ----------
// Renders static text with an animated light-reflection effect.
interface ReflectionTextProps {
  text: string;
  variant?: TypographyProps["variant"];
}
const ReflectionText: React.FC<ReflectionTextProps> = ({
  text,
  variant = "body1",
}) => {
  const paragraphs = text.split("\n").filter((p) => p.trim() !== "");
  return (
    <>
      <GlobalStyles
        styles={`
          @keyframes shine {
            0% { left: -100%; }
            50% { left: 100%; }
            100% { left: 100%; }
          }
        `}
      />
      {paragraphs.map((para, index) => (
        <Typography
          key={index}
          variant={variant}
          sx={{
            position: "relative",
            display: "inline",
            overflow: "hidden",
            color: theme.palette.custom.textPrimary,
            whiteSpace: "pre-wrap",
            "&::after": {
              content: '""',
              position: "absolute",
              top: 0,
              left: "-30%",
              width: "30%",
              height: "100%",
              background: `linear-gradient(
             90deg,
             transparent,
             ${theme.palette.secondary.main}44,
             transparent
           )`,
              transform: "skewX(-20deg)",
              animation: "shine 3s ease-in-out infinite",
            },
          }}
        >
          {para}
        </Typography>
      ))}
    </>
  );
};

// ---------- StepPanel Component ----------
// A reusable panel that displays an instruction, an input element, and a caption.
// If the instructionVariant is "h4", it uses TypewriterTitle; otherwise, it uses ReflectionText.
interface StepPanelProps {
  instruction: string;
  input: React.ReactNode;
  caption: string;
  instructionVariant?: TypographyProps["variant"];
}
export const StepPanel: React.FC<StepPanelProps> = ({
  instruction,
  input,
  caption,
  instructionVariant,
}) => {
  return (
    <Box
      sx={{
        width: "100%",
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(3),
      }}
    >
      {/* {instructionVariant === "h4" ? (
        <TypewriterTitle text={instruction} variant={instructionVariant} />
      ) : ( */}
      <ReflectionText text={instruction} variant={instructionVariant || "h6"} />
      {/* )} */}
      {input}
      {caption && (
        <Typography
          variant="caption"
          sx={{
            color: theme.palette.custom.textPrimary,
            lineHeight: 1.4,
            pb: theme.spacing(1),
          }}
        >
          {caption}
        </Typography>
      )}
    </Box>
  );
};

const LoadingScreenCompanyCreation: React.FC = () => {
  const { t } = useTranslation();
  const loadingMessages = [
    t("company_creation.loading2", "Building Your Starters..."),
    t("company_creation.loading3", "Mapping Sustainable Activities..."),
    t("company_creation.loading4", "Gathering Evidences..."),
    t("company_creation.loading5", "Setting Up Your Company Details..."),
  ];
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);

  useEffect(() => {
    if (currentMessageIndex < loadingMessages.length - 1) {
      const timer = setTimeout(() => {
        setCurrentMessageIndex(currentMessageIndex + 1);
      }, 4000); // Wait 4 seconds for each message
      return () => clearTimeout(timer);
    }
  }, [currentMessageIndex, loadingMessages.length]);

  return (
    <>
      {FadeInStyles}
      <Box
        tabIndex={0}
        sx={{
          width: "100%",
          maxWidth: "500px",
          minHeight: "100vh",
          bgcolor: "transparent", //theme.palette.background.default,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          p: theme.spacing(2),
          mx: "auto",
        }}
      >
        <CircularProgress />
        <Box
          sx={{
            width: "100%",
            textAlign: "center",
            animation: "fadeIn 2s ease-in-out",
            pt: theme.spacing(5),
          }}
        >
          <TypewriterTitle
            text={loadingMessages[currentMessageIndex]}
            variant="h6"
          />
        </Box>
      </Box>
    </>
  );
};

// ---------- Main CompanyCreationTemplate Component ----------
// This component is now self-contained: it initializes its own state, fetches available environments,
// and implements the creation logic.
const CompanyCreationTemplate: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // Local state for the multi-step form
  const [step, setStep] = useState(0);
  const [newCompany, setNewCompany] = useState<Partial<PlatformUser>>({
    name: "",
    description: "",
  });

  const [availableSectors, setAvailableSectors] = useState<Sector[]>([]);
  const [selectedSectorId, setSelectedSectorId] = useState<string>("");

  const [selectedEnvIds, setSelectedEnvIds] = useState<string[]>([]);
  const [availableEnvironments, setAvailableEnvIds] = useState<Environment[]>(
    []
  );
  const [loading, setLoading] = useState(true);
  const isSingleEnvironment = availableEnvironments.length === 1;

  const finalStep = isSingleEnvironment ? 3 : 4;

  // fetch environments + sectors
  useEffect(() => {
    (async () => {
      try {
        const [envs, sectors] = await Promise.all([
          fetchData<Environment[]>("/environments/"),
          fetchData<Sector[]>("/sectors/"),
        ]);
        setAvailableEnvIds(envs);
        setAvailableSectors(sectors);
        if (envs.length === 1) setSelectedEnvIds([envs[0].id]);
      } catch (error) {
        logger.error(error);
      }
      setLoading(false);
    })();
  }, []);

  // For step 0, auto-advance after 5 seconds.
  useEffect(() => {
    if (step === 0) {
      const timer = setTimeout(() => {
        setStep(1);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [step]);

  const handleContinue = () => {
    if (step < 3) {
      setStep(step + 1);
    } else {
      handleCreateCompany();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" && step !== 0 && !continueDisabled) {
      handleContinue();
    }
  };

  let continueDisabled = false;
  if (step === 1 && (!newCompany.name || newCompany.name.trim() === "")) {
    continueDisabled = true;
  }
  if (
    step === 2 &&
    (!newCompany.description || newCompany.description.trim() === "")
  ) {
    continueDisabled = true;
  }
  if (step === 3 && !selectedSectorId) continueDisabled = true;
  if (step === finalStep && selectedEnvIds.length === 0)
    continueDisabled = true;

  // Handler for creating the new company (platform user)
  const handleCreateCompany = async () => {
    const { sector, ...rest } = newCompany;

    if (!newCompany.name?.trim()) {
      enqueueSnackbar(
        t("platform_users.create_name_required", "Company name is required"),
        { variant: "warning" }
      );
      return;
    }
    try {
      setLoading(true);
      const payload = {
        ...newCompany,
        sector: selectedSectorId,
        environments: selectedEnvIds,
      };
      const platform_user: PlatformUser = await createData(
        "/platform_users/",
        payload
      );
      enqueueSnackbar(
        t("add_user.success_message", "Company created successfully!"),
        { variant: "success" }
      );
      // Reset form state
      setNewCompany({ name: "", description: "" });
      setSelectedEnvIds([]);
      // Navigate to dashboard after creation
      navigate("/dashboard");
    } catch (error) {
      enqueueSnackbar(t("add_user.error_message", "Error creating company"), {
        variant: "error",
      });
    }
    setLoading(false);
  };

  const renderStepPanel = () => {
    switch (step) {
      case 0:
        return (
          <Box
            key={step}
            sx={{
              width: "100%",
              maxWidth: "500px",

              textAlign: "center",
              animation: "fadeIn 2s ease-in-out",
            }}
          >
            <TypewriterTitle
              text={t(
                "company_creation.header",
                "Welcome to Biosphere’s Certification Process Hub"
              )}
              variant="h4"
            />
          </Box>
        );
      case 1:
        return (
          <Box
            key={step}
            sx={{
              width: "100%",
              maxWidth: "500px",

              textAlign: "center",
              animation: "fadeIn 2s ease-in-out",
            }}
          >
            <StepPanel
              instruction={t(
                "company_creation.name_instruction",
                "Please enter your company name"
              )}
              input={
                <TextField
                  fullWidth
                  autoFocus
                  placeholder={t(
                    "company_creation.example_company",
                    "e.g. ACME Inc."
                  )}
                  value={newCompany.name || ""}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setNewCompany({ ...newCompany, name: e.target.value })
                  }
                  variant="standard"
                  InputProps={{
                    disableUnderline: true,
                    style: { textAlign: "center", fontSize: "1.1rem" },
                  }}
                  sx={{
                    borderBottom: "1px solid #ECECEC",
                    mb: theme.spacing(2),
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !continueDisabled) {
                      handleContinue();
                    }
                  }}
                />
              }
              caption={t(
                "company_creation.name_caption",
                "This name will identify your company."
              )}
            />
          </Box>
        );
      case 2:
        return (
          <Box
            key={step}
            sx={{
              width: "100%",
              maxWidth: "500px",

              textAlign: "center",
              animation: "fadeIn 0.8s ease-in-out",
            }}
          >
            <StepPanel
              instruction={t(
                "company_creation.description_instruction",
                "Please provide a brief description of your company"
              )}
              input={
                <TextField
                  fullWidth
                  autoFocus
                  placeholder={t(
                    "company_creation.example_description",
                    "e.g. We build quality products."
                  )}
                  value={newCompany.description || ""}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setNewCompany({
                      ...newCompany,
                      description: e.target.value,
                    })
                  }
                  variant="standard"
                  InputProps={{
                    disableUnderline: true,
                    style: { textAlign: "center", fontSize: "1.1rem" },
                  }}
                  sx={{
                    borderBottom: "1px solid #ECECEC",
                    mb: theme.spacing(2),
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !continueDisabled) {
                      handleContinue();
                    }
                  }}
                />
              }
              caption={t(
                "company_creation.description_instruction_caption",
                "Provide a concise overview of what your company does."
              )}
            />
          </Box>
        );
      case 3:
        return (
          <Box
            key={step}
            sx={{
              width: "100%",
              maxWidth: "500px",

              textAlign: "center",
              animation: "fadeIn 0.8s ease-in-out",
            }}
          >
            <StepPanel
              instruction={t(
                "company_creation.sector_caption",
                "Selecciona el sector que mejor represente tu empresa; esto definirá el plan de acción personalizado que te propondremos."
              )}
              input={
                <FormControl
                  variant="standard"
                  fullWidth
                  sx={{
                    borderBottom: "1px solid #ECECEC",
                    mb: theme.spacing(2),
                    width: "100%",
                  }}
                >
                  <InputLabel
                    sx={{ textAlign: "center", width: "100%", px: 0.5 }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !continueDisabled) {
                        handleContinue();
                      }
                    }}
                  >
                    {/* {t("platform_users.select_sector_label")} */}
                  </InputLabel>

                  <Select
                    value={selectedSectorId}
                    onChange={(e) =>
                      setSelectedSectorId(e.target.value as string)
                    }
                    variant="standard"
                    sx={{
                      textAlign: "center",
                      "& .MuiSelect-select": { padding: theme.spacing(1) },
                      borderBottom: "none",
                      px: theme.spacing(4),
                    }}
                  >
                    {availableSectors.map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        {s.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              }
              caption=""
            />
          </Box>
        );
      case 4:
        return (
          <Box
            key={step}
            sx={{
              width: "100%",
              maxWidth: "500px",

              textAlign: "center",
              animation: "fadeIn 0.8s ease-in-out",
            }}
          >
            <StepPanel
              instruction={t(
                "company_creation.environment_instruction",
                "Please select the environment(s) applicable to your company"
              )}
              input={
                <FormControl
                  variant="standard"
                  sx={{
                    borderBottom: "1px solid #ECECEC",
                    mb: theme.spacing(2),
                    width: "100%",
                  }}
                >
                  <InputLabel
                    sx={{ textAlign: "center", width: "100%", px: 0.5 }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !continueDisabled) {
                        handleContinue();
                      }
                    }}
                  >
                    {t(
                      "platform_users.select_environments_label",
                      "Select Environments"
                    )}{" "}
                    {isSingleEnvironment && t("platform_users.auto_assigned")}
                  </InputLabel>
                  <Select
                    multiple
                    value={selectedEnvIds}
                    disabled={isSingleEnvironment}
                    onChange={(e: any) => {
                      const value =
                        typeof e.target.value === "string"
                          ? e.target.value.split(",")
                          : e.target.value;
                      setSelectedEnvIds(value);
                    }}
                    renderValue={(selected) =>
                      (selected as string[])
                        .map(
                          (id) =>
                            availableEnvironments.find((env) => env.id === id)
                              ?.name
                        )
                        .join(", ")
                    }
                    sx={{
                      textAlign: "center",
                      "& .MuiSelect-select": { padding: theme.spacing(1) },
                      borderBottom: "none",
                      px: theme.spacing(4),
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !continueDisabled) {
                        handleContinue();
                      }
                    }}
                  >
                    {availableEnvironments.map((env) => (
                      <MenuItem key={env.id} value={env.id}>
                        {env.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              }
              caption={t("company.choose_environments", "Choose one or more holding companies (environments).")}
            />
          </Box>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <>
        <LoadingScreenCompanyCreation></LoadingScreenCompanyCreation>
      </>
    );
  }

  return (
    <>
      {FadeInStyles}
      <Box
        tabIndex={0}
        onKeyDown={handleKeyDown}
        sx={{
          width: "100%",
          // maxWidth: "500px",
          minHeight: "100vh",
          bgcolor: theme.palette.background.default,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          p: theme.spacing(2),
          mx: "auto",
        }}
      >
        {renderStepPanel()}
        {/* For steps 1-3, show Continue button */}
        {step !== 0 && (
          <Button
            variant="contained"
            onClick={handleContinue}
            disabled={continueDisabled}
            sx={{
              mb: theme.spacing(3),
              px: theme.spacing(2),
              py: theme.spacing(1),
              borderRadius: "8px",
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.common.white,
              textTransform: "uppercase",
              fontSize: "1rem",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: theme.palette.custom.primary,
                color: theme.palette.primary.main,
              },
            }}
          >
            {step < finalStep
              ? t("common.continue", "Continue")
              : t("platform_users.create_button", "Create Company")}
          </Button>
        )}
      </Box>
    </>
  );
};

export default CompanyCreationTemplate;
